import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RichMarkdownEditor } from "@/components/RichMarkdownEditor";
import { ArrowLeft, FileDown, Plus, Trash2, Receipt, Lock, Send, Share2, FileText, Clock, Check, Calendar, MapPin, ChevronRight, Mic, Square, Loader2 } from "lucide-react";
import { useGuard, useUnsavedGuard } from "@/context/GuardContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PaiementsTab from "@/components/PaiementsTab";
import AnatomyTab from "@/components/AnatomyTab";
import { SearchSelectField } from "@/components/SearchSelect";
import { VoiceRecorderButton } from "@/components/VoiceRecorderButton";

const STATUTS = ["Planifié", "Soin délivré", "À clôturer", "Clôturé", "Annulé"];
const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const lineTtc = (l) => (Number(l.qte) || 0) * (Number(l.prix_unitaire) || 0) * (1 + (Number(l.tva) || 0) / 100);

export default function FicheSoin() {
  const { id } = useParams();
  const { attempt } = useGuard();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const anatRef = useRef(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["paiements", "facture", "anatomie"].includes(params.get("tab")) ? params.get("tab") : "soin");
  const [soin, setSoin] = useState(null);
  const [patient, setPatient] = useState(null);
  const [rdv, setRdv] = useState(null);
  const [md, setMd] = useState("");
  const [initialMd, setInitialMd] = useState("");
  const [templates, setTemplates] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [tags, setTags] = useState([]);
  const [ready, setReady] = useState(false);
  const editorRef = useRef(null);
  // Facture
  const [lignes, setLignes] = useState([]);
  const [factInitial, setFactInitial] = useState("[]");
  const [summary, setSummary] = useState({ total_ttc: 0, total_paye: 0, solde: 0, verrouillee: false });
  const [prestations, setPrestations] = useState([]);
  const [saveDlg, setSaveDlg] = useState(false); // eslint-disable-line
  const [shareDlg, setShareDlg] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [psSignature, setPsSignature] = useState("");
  const [shareCR, setShareCR] = useState(false);
  const [shareFact, setShareFact] = useState(true);
  const [trameDlg, setTrameDlg] = useState(false);
  const [trameSel, setTrameSel] = useState("");
  const [trameDefaut, setTrameDefaut] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [changeDlg, setChangeDlg] = useState(false);
  const appliedDefault = useRef(false);
  const [saveState, setSaveState] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const autoTimer = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/soins/${id}`);
    setSoin(data); setMd(data.contenu_md || ""); setInitialMd(data.contenu_md || "");
    if (data.patient_id) api.get(`/patients/${data.patient_id}`).then((r) => setPatient(r.data)).catch(() => {});
    if (data.rdv_id) api.get(`/rdvs/${data.rdv_id}`).then((r) => setRdv(r.data)).catch(() => {});
  };
  const loadFacture = async () => {
    const { data } = await api.get(`/soins/${id}/facture`);
    setLignes(data.lignes || []); setFactInitial(JSON.stringify(data.lignes || []));
    setSummary({ total_ttc: data.total_ttc, total_paye: data.total_paye, solde: data.solde, verrouillee: data.verrouillee });
  };
  const reloadSummary = async () => {
    const { data } = await api.get(`/soins/${id}/facture`);
    setSummary({ total_ttc: data.total_ttc, total_paye: data.total_paye, solde: data.solde, verrouillee: data.verrouillee });
  };
  useEffect(() => {
    setReady(false); appliedDefault.current = false;
    load(); loadFacture();
    api.get("/settings/soin-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/prestations").then((r) => setPrestations(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => setCareTypes(r.data)).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/settings/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/patients").then((r) => setAllPatients(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setPsSignature(r.data?.signature_email || ""); setTrameDefaut(r.data?.trame_defaut || ""); }).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  // La trame par défaut est appliquée à la CRÉATION du RDV/soin (côté serveur), jamais à l'ouverture.
  const reassignPatient = async (pid) => {
    try { await api.put(`/soins/${id}/patient`, { patient_id: pid }); setChangeDlg(false); toast.success("Patient ré-associé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (tab === "paiements") {
      (async () => {
        if (!summary.verrouillee && JSON.stringify(lignes) !== factInitial) {
          const { data } = await api.put(`/soins/${id}/facture`, { lignes });
          setFactInitial(JSON.stringify(data.lignes || []));
        }
        reloadSummary();
      })();
    } else if (tab === "facture") {
      reloadSummary();
    }
    /* eslint-disable-next-line */
  }, [tab]);

  const latest = useRef({ md: "", titre: "", tags: [] });
  latest.current = { md, titre: soin?.titre || "", tags: soin?.tags || [] };
  const doAutosave = async () => {
    setSaveState("saving");
    try { await api.put(`/soins/${id}`, { contenu_md: latest.current.md, titre: latest.current.titre, tags: latest.current.tags }); setInitialMd(latest.current.md); setSaveState("saved"); setSavedAt(Date.now()); }
    catch (e) { setSaveState("error"); }
  };
  const scheduleSave = () => { if (autoTimer.current) clearTimeout(autoTimer.current); autoTimer.current = setTimeout(doAutosave, 700); };
  const saveSoin = doAutosave;
  const saveSuivi = (patch) => { setSoin((s) => ({ ...s, ...patch })); api.put(`/soins/${id}`, patch).catch(() => toast.error("Enregistrement du suivi impossible")); };
  const handleMd = (v) => { setMd(v); if (!ready) { setInitialMd(v); setReady(true); } else scheduleSave(); };
  const insertNote = (text) => {
    if (!text || !text.trim()) return;
    const t = text.trim();
    const ed = editorRef.current;
    if (ed?.insertMarkdown) { ed.insertMarkdown(t.endsWith(" ") ? t : t + " "); ed.focus?.(() => {}, { defaultSelection: "rootEnd" }); setReady(true); scheduleSave(); return; }
    const base = latest.current.md || "";
    const next = (base ? base.replace(/\s*$/, "") + "\n\n" : "") + t;
    setMd(next); editorRef.current?.setMarkdown?.(next); setReady(true); scheduleSave();
  };
  const applyTrame = async (withPrestations) => {
    const t = templates.find((x) => x.id === trameSel); if (!t) return;
    const next = t.contenu_md || "";
    setMd(next); editorRef.current?.setMarkdown?.(next); setReady(true); setTrameDlg(false);
    if (withPrestations) { try { await insertTramePrestations(t); } catch (_) { /* ignore */ } }
  };
  const insertTramePrestations = async (tmpl) => {
    const ps = tmpl?.prestations || [];
    if (!ps.length || summary.verrouillee) return;
    const today = new Date().toISOString().slice(0, 10);
    const add = ps.map((p) => ({ date: today, libelle: p.libelle || "", qte: p.qte || 1, prix_unitaire: p.prix_unitaire || 0, tva: p.tva || 0, prestation_id: p.prestation_id || null }));
    const { data } = await api.put(`/soins/${id}/facture`, { lignes: [...lignes, ...add] });
    setLignes(data.lignes || []); setFactInitial(JSON.stringify(data.lignes || []));
    setSummary({ total_ttc: data.total_ttc, total_paye: data.total_paye, solde: data.solde, verrouillee: data.verrouillee });
  };
  const saveFacture = async (verrouiller = false) => {
    const { data } = await api.put(`/soins/${id}/facture`, { lignes, verrouiller });
    setLignes(data.lignes || []); setFactInitial(JSON.stringify(data.lignes || []));
    setSummary({ total_ttc: data.total_ttc, total_paye: data.total_paye, solde: data.solde, verrouillee: data.verrouillee });
    toast.success(verrouiller ? "Facture enregistrée et verrouillée" : "Facture enregistrée");
  };
  const lockFacture = async () => {
    if (!(await confirm({ title: "Verrouiller la facture", message: "Une facture verrouillée passe en lecture seule : seules les opérations de paiement resteront possibles. Continuer ?", confirmLabel: "Verrouiller", danger: false }))) return;
    await saveFacture(true);
  };

  const soinDirty = md !== initialMd;
  const factDirty = JSON.stringify(lignes) !== factInitial;
  useUnsavedGuard(
    tab === "soin" ? soinDirty : tab === "facture" ? factDirty : false,
    tab === "soin" ? saveSoin : tab === "facture" ? (() => saveFacture(false)) : async () => {}
  );

  // Enregistre les modifications en cours (soin + facture) avant export.
  const persistBeforeExport = async () => {
    if (md !== initialMd) await saveSoin();
    if (!summary.verrouillee && JSON.stringify(lignes) !== factInitial) {
      const { data } = await api.put(`/soins/${id}/facture`, { lignes });
      setFactInitial(JSON.stringify(data.lignes || []));
      setSummary({ total_ttc: data.total_ttc, total_paye: data.total_paye, solde: data.solde, verrouillee: data.verrouillee });
    }
  };
  const openShare = async () => {
    if (md !== initialMd) { try { await saveSoin(); } catch (_) { /* ignore */ } }
    const d = soin.date ? new Date(soin.date).toLocaleDateString("fr-FR") : "";
    const t = (soin.titre || "").trim();
    const base = `Bonjour,\n\nVoici les documents concernant le soin du ${d}${t ? `, ${t}` : ""}.`;
    setShareMsg(psSignature ? `${base}\n\n${psSignature}` : `${base}\n\nBien à vous.`);
    setShareEmail(patient?.email || ""); setShareCR(false); setShareFact(true); setShareDlg(true);
  };
  const doShare = async () => {
    if (!shareEmail) { toast.error("Saisissez un email"); return; }
    if (!shareCR && !shareFact) { toast.error("Sélectionnez au moins un document"); return; }
    try {
      await persistBeforeExport();
      await api.post(`/soins/${id}/documents/send`, { email: shareEmail, message: shareMsg, include_compte_rendu: shareCR, include_facture: shareFact, origin_url: window.location.origin });
      toast.success("Documents envoyés par email"); setShareDlg(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadOne = async (path, filename) => {
    const w = window.open("", "_blank");
    try {
      await persistBeforeExport();
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (w && !w.closed) { w.location = url; }
      else { const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); }
    } catch (e) { try { w && w.close(); } catch (_) { /* noop */ } toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDownload = async () => {
    const docs = [];
    if (shareCR) docs.push([`/soins/${id}/compte-rendu`, "compte-rendu.pdf"]);
    if (shareFact) docs.push([`/soins/${id}/facture/pdf`, "facture.pdf"]);
    if (docs.length === 0) { toast.error("Sélectionnez au moins un document"); return; }
    const wins = docs.map(() => window.open("", "_blank"));
    try {
      await persistBeforeExport();
      for (let i = 0; i < docs.length; i++) {
        const r = await api.get(docs[i][0], { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        if (wins[i] && !wins[i].closed) { wins[i].location = url; }
        else { const a = document.createElement("a"); a.href = url; a.download = docs[i][1]; document.body.appendChild(a); a.click(); a.remove(); }
      }
      setShareDlg(false);
    } catch (e) {
      wins.forEach((w) => { try { w && w.close(); } catch (_) { /* noop */ } });
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const saveRdv = async (patch) => {
    const r = { ...rdv, ...patch };
    await api.put(`/rdvs/${rdv.id}`, { patient_id: r.patient_id, date: r.date, duree_min: r.duree_min || 60, type_rdv: r.type_rdv, type_soin: r.type_soin, statut: r.statut, montant_du: r.montant_du || 0, titre: r.titre || "", lieu: r.lieu || "cabinet", secteur_id: r.secteur_id || null, origine: r.origine || "direct" });
    setRdv(r); toast.success("RDV mis à jour");
  };

  // Facture line ops
  const addLine = (prest) => {
    const base = prest ? { libelle: prest.libelle, prix_unitaire: prest.prix_unitaire, tva: prest.tva, prestation_id: prest.id, reference: prest.reference || "" } : { libelle: "", prix_unitaire: 0, tva: 0, prestation_id: null, reference: "" };
    setLignes([...lignes, { date: new Date().toISOString().slice(0, 10), qte: 1, ...base }]);
  };
  const updLine = (i, k, v) => setLignes(lignes.map((l, idx) => {
    if (idx !== i) return l;
    if (k === "ttc") {
      const qte = Number(l.qte) || 0; const tva = Number(l.tva) || 0;
      const denom = qte * (1 + tva / 100);
      const pu = denom ? (Number(v) || 0) / denom : 0;
      return { ...l, prix_unitaire: Math.round(pu * 100) / 100 };
    }
    return { ...l, [k]: v };
  }));
  const delLine = (i) => setLignes(lignes.filter((_, idx) => idx !== i));

  if (!soin) return <div className="text-slate-400">Chargement…</div>;
  const back = () => attempt(soin.patient_id ? `/patients/${soin.patient_id}` : "/agenda");
  const patientName = patient ? `${patient.prenom} ${patient.nom}` : "";
  const draftTotal = lignes.reduce((s, l) => s + lineTtc(l), 0);
  const draftSolde = draftTotal - (summary.total_paye || 0);
  const ro = !!summary.verrouillee;
  const anatCount = (() => { const a = soin.anatomie; if (!a || typeof a !== "object") return 0; return Object.values(a).reduce((n, v) => n + (Array.isArray(v) ? v.length : 0), 0); })();
  const tabBadge = { soin: "", anatomie: anatCount ? `${anatCount} repère${anatCount > 1 ? "s" : ""}` : "", facture: summary.verrouillee ? "verrouillée" : "", paiements: summary.total_ttc > 0 ? (summary.solde <= 0.009 ? "payé" : (summary.total_paye > 0 ? "partiel" : "à régler")) : "" };
  const prevSoin = (patient?.soins || []).filter((s) => s.id !== soin.id && s.date && (!soin.date || s.date < soin.date)).sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  const nextSoin = (patient?.soins || []).filter((s) => s.id !== soin.id && s.date && soin.date && s.date > soin.date).sort((a, b) => (a.date < b.date ? -1 : 1))[0];
  const trameFacture = templates.find((t) => t.id === trameSel && (t.prestations || []).length)
    || templates.find((t) => t.par_defaut && (t.prestations || []).length)
    || templates.find((t) => (t.prestations || []).length);

  return (
    <div>
      <button onClick={back} className="inline-flex items-center gap-1.5 text-[12px] mb-3" style={{ color: "var(--muted)" }}><ArrowLeft className="w-3.5 h-3.5" />Patients › {patientName || "Fiche patient"}</button>

      <div className="mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[12.5px]" style={{ color: "var(--muted-2)" }}>Fiche de soin · <span className="mono">{soin.id}</span></div>
            <InlineTitle value={soin.titre || ""} onChange={(v) => setSoin({ ...soin, titre: v })} onCommit={() => doAutosave()} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="fs-share" onClick={openShare} className="sx-btn-secondary"><Share2 className="w-4 h-4" />Partager</button>
            {tab === "facture" && !summary.verrouillee && <button data-testid="fs-lock-facture" onClick={lockFacture} className="sx-btn-secondary"><Lock className="w-4 h-4" />Verrouiller</button>}
            <button data-testid="fs-save" onClick={() => { if (tab === "anatomie") anatRef.current?.save(); else if (tab === "facture") saveFacture(false); else doAutosave(); }} className="sx-btn-auto"><Check className="w-4 h-4" />Enregistrer (automatique)</button>
            <SaveIndicator state={saveState} at={savedAt} />
          </div>
        </div>

        {rdv && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button data-testid="fs-trame-btn" onClick={() => { setTrameSel(templates[0]?.id || ""); setTrameDlg(true); }} className="sx-info sx-info-dark"><FileText className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />Trame{soin.trame_nom ? ` : ${soin.trame_nom}` : ""}<ChevronRight className="w-3 h-3" /></button>
            <span className="sx-info"><Calendar className="w-3.5 h-3.5" />{rdv.date ? new Date(rdv.date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
            <span className="sx-info"><select data-testid="fs-rdv-type" value={rdv.type_soin || ""} onChange={(e) => saveRdv({ type_soin: e.target.value })} className="bg-transparent outline-none" style={{ color: "inherit" }}><option value="">Type…</option>{careTypes.map((c) => <option key={c.id} value={c.id}>{c.nom || c.label}</option>)}</select></span>
            <span className="sx-info"><MapPin className="w-3.5 h-3.5" /><select data-testid="fs-rdv-secteur" value={rdv.secteur_id || ""} onChange={(e) => saveRdv({ secteur_id: e.target.value })} className="bg-transparent outline-none"><option value="">Secteur…</option>{secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom || s.label}</option>)}</select></span>
            <span className="sx-info"><Clock className="w-3.5 h-3.5" /><DureeTool value={rdv.duree_min || 60} startISO={rdv.date} onSet={(m) => saveRdv({ duree_min: m })} /></span>
            <span className="sx-info"><select data-testid="fs-rdv-statut" value={rdv.statut} onChange={(e) => saveRdv({ statut: e.target.value })} className="bg-transparent outline-none">{STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></span>
            <span className="mono text-[11.5px]" style={{ color: "var(--muted-2)" }}>{rdv.id}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6">
        <div className="min-w-0">
      <div className="sx-seg mb-4">
        {[["soin", "Soin"], ["anatomie", "Anatomie"], ["facture", "Facture"], ["paiements", "Paiements"]].map(([k, l]) => (
          <button key={k} data-testid={`fs-tab-${k}`} onClick={() => setTab(k)} className="sx-seg-item" data-active={tab === k}>{l}{tabBadge[k] && <span className="ml-1.5 text-[10.5px] font-normal" data-testid={`fs-tab-badge-${k}`} style={{ color: "var(--muted-2)" }}>{tabBadge[k]}</span>}</button>
        ))}
      </div>

      <div className={tab === "soin" ? "" : "hidden"}>
        <VoiceControls soinId={id} onInsert={insertNote} />
        <div className="bg-white sx-card overflow-hidden">
          <RichMarkdownEditor ref={editorRef} markdown={md} onChange={handleMd} tags={tags} />
        </div>
      </div>

      <div className={tab === "anatomie" ? "" : "hidden"}>
        <AnatomyTab ref={anatRef} soinId={id} initial={soin.anatomie} onSaved={(anat) => setSoin((s) => ({ ...s, anatomie: anat }))} />
      </div>

      {tab === "paiements" && <PaiementsTab soin={soin} summary={summary} patientEmail={patient?.email} patientPhone={patient?.telephone} onChange={reloadSummary} />}

      {tab === "facture" && (
        <div className="bg-white sx-card" data-testid="fs-facture-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-5 h-5" />Facture{summary.verrouillee && <span data-testid="fact-locked-badge" className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-600"><Lock className="w-3 h-3" />Verrouillée</span>}</h3>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-400">Total TTC</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="fact-total">{eur(draftTotal)}</div>
            </div>
          </div>

          <div className="overflow-x-auto px-3 py-2">
            <table className="w-full text-[13px]" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider" style={{ color: "var(--muted-2)" }}>
                  <th className="px-2 py-1.5 font-semibold" style={{ width: 118 }}>Date</th>
                  <th className="px-2 py-1.5 font-semibold">Libellé</th>
                  <th className="px-2 py-1.5 font-semibold" style={{ width: 96 }}>Réf</th>
                  <th className="px-2 py-1.5 font-semibold text-right" style={{ width: 58 }}>Qté</th>
                  <th className="px-2 py-1.5 font-semibold text-right" style={{ width: 88 }}>PU (€)</th>
                  <th className="px-2 py-1.5 font-semibold text-right" style={{ width: 64 }}>TVA %</th>
                  <th className="px-2 py-1.5 font-semibold text-right" style={{ width: 100 }}>Total TTC</th>
                  <th className="px-1 py-1.5" style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: "var(--faint)" }}>Aucune ligne. Ajoutez une prestation ci-dessous.</td></tr>
                ) : lignes.map((l, i) => (
                  <tr key={i} data-testid={`fact-line-${i}`}>
                    <td className="sx-fact-cell"><input type="date" disabled={ro} value={l.date || ""} onChange={(e) => updLine(i, "date", e.target.value)} className="sx-fact-input mono" /></td>
                    <td className="sx-fact-cell"><input data-testid={`fact-libelle-${i}`} disabled={ro} value={l.libelle} onChange={(e) => updLine(i, "libelle", e.target.value)} placeholder="Libellé…" className="sx-fact-input" /></td>
                    <td className="sx-fact-cell"><input data-testid={`fact-ref-${i}`} disabled={ro} value={l.reference || ""} onChange={(e) => updLine(i, "reference", e.target.value)} className="sx-fact-input mono" /></td>
                    <td className="sx-fact-cell"><input type="number" step="0.5" data-testid={`fact-qte-${i}`} disabled={ro} value={l.qte} onChange={(e) => updLine(i, "qte", e.target.value)} className="sx-fact-input mono text-right" /></td>
                    <td className="sx-fact-cell"><input type="number" step="0.01" data-testid={`fact-pu-${i}`} disabled={ro} value={l.prix_unitaire} onChange={(e) => updLine(i, "prix_unitaire", e.target.value)} className="sx-fact-input mono text-right" /></td>
                    <td className="sx-fact-cell"><input type="number" step="0.1" data-testid={`fact-tva-${i}`} disabled={ro} value={l.tva} onChange={(e) => updLine(i, "tva", e.target.value)} className="sx-fact-input mono text-right" /></td>
                    <td className="sx-fact-cell"><TtcCell l={l} ro={ro} testid={`fact-ttc-${i}`} onCommit={(v) => updLine(i, "ttc", v)} /></td>
                    <td className="sx-fact-cell text-center">{!ro && <button data-testid={`fact-del-${i}`} onClick={() => delLine(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!ro && (
          <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <button data-testid="fact-add-line" onClick={() => addLine(null)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"><Plus className="w-4 h-4" />Ligne libre</button>
            {prestations.length > 0 && (
              <select data-testid="fact-add-prestation" defaultValue="" onChange={(e) => { const p = prestations.find((x) => x.id === e.target.value); if (p) addLine(p); e.target.value = ""; }} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">+ Depuis le catalogue…</option>
                {prestations.map((p) => <option key={p.id} value={p.id}>{`${p.libelle} — ${eur(p.prix_unitaire)}`}</option>)}
              </select>
            )}
            {lignes.length === 0 && trameFacture && (
              <button data-testid="fact-from-trame" onClick={() => insertTramePrestations(trameFacture)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium" style={{ background: "var(--bg-warm)", color: "var(--ink)" }}>
                <FileText className="w-4 h-4" style={{ color: "var(--accent)" }} />Proposer depuis la trame « {trameFacture.nom} »
              </button>
            )}
          </div>
          )}

          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
            <div className="flex justify-end gap-10 text-sm">
              <div className="text-right">
                <div className="text-slate-500">Montant total à régler</div>
                <div className="text-lg font-bold text-slate-900" data-testid="fact-a-regler">{eur(draftTotal)}</div>
              </div>
              <div className="text-right">
                <div className="text-slate-500">Solde à régler</div>
                <div className={`text-lg font-bold ${draftSolde > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="fact-solde">{eur(draftSolde)}</div>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
        <aside className="hidden lg:block">
          <FicheSideCol patient={patient} summary={summary} liveTotal={draftTotal} liveSolde={draftSolde} prevSoin={prevSoin} nextSoin={nextSoin} soin={soin} eur={eur}
            onSaveSuivi={saveSuivi}
            onOpenPatient={() => soin.patient_id && navigate(`/patients/${soin.patient_id}`)}
            onOpenPrev={(sid) => navigate(`/soins/${sid}`)} onOpenNext={(sid) => navigate(`/soins/${sid}`)} />
        </aside>
      </div>

      <Dialog open={shareDlg} onOpenChange={(o) => !o && setShareDlg(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Partager les documents</DialogTitle>
            <DialogDescription>Sélectionnez les documents à envoyer au patient par email et joignez un mot personnalisé.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium text-slate-700">Documents</span>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input data-testid="fs-share-cr" type="checkbox" checked={shareCR} onChange={(e) => setShareCR(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
                  Compte rendu de soin (PDF)
                </label>
                <button data-testid="fs-share-dl-cr" onClick={() => downloadOne(`/soins/${id}/compte-rendu`, "compte-rendu.pdf")} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"><FileDown className="w-3.5 h-3.5" />Télécharger</button>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input data-testid="fs-share-fact" type="checkbox" checked={shareFact} onChange={(e) => setShareFact(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
                  Facture (PDF)
                </label>
                <button data-testid="fs-share-dl-fact" onClick={() => downloadOne(`/soins/${id}/facture/pdf`, "facture.pdf")} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"><FileDown className="w-3.5 h-3.5" />Télécharger</button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Email du destinataire</label>
              <input data-testid="fs-share-email" type="email" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Message</label>
              <textarea data-testid="fs-share-msg" value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} rows={4} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShareDlg(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="fs-share-download" onClick={doDownload} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium hover:bg-[#232833]"><FileDown className="w-4 h-4" />Télécharger</button>
            <button data-testid="fs-share-confirm" onClick={doShare} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium hover:bg-[#232833]"><Send className="w-4 h-4" />Envoyer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={trameDlg} onOpenChange={(o) => !o && setTrameDlg(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Insérer une trame</DialogTitle>
            <DialogDescription>Choisissez une trame, puis ajoutez-la au texte existant ou remplacez-le entièrement.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium text-slate-700">Trame</label>
            <select data-testid="fs-trame-select" value={trameSel} onChange={(e) => setTrameSel(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
            </select>
          </div>
          <DialogFooter>
            <button onClick={() => setTrameDlg(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="fs-trame-replace" onClick={() => applyTrame(false)} className="px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-medium">Remplacer que la fiche de soin</button>
            <button data-testid="fs-trame-all" onClick={() => applyTrame(true)} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium">Tout appliquer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={changeDlg} onOpenChange={(o) => !o && setChangeDlg(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changer le patient</DialogTitle>
            <DialogDescription>Ré-associe ce RDV, le soin et les paiements/factures liés au patient choisi.</DialogDescription>
          </DialogHeader>
          <SearchSelectField label="Patient" testid="fs-change-patient-select" dialogTitle="Rechercher un patient" items={allPatients} value={rdv?.patient_id || ""} getLabel={(pp) => `${pp.prenom} ${pp.nom}`} getSub={(pp) => pp.telephone || pp.email} onChange={(pid) => reassignPatient(pid)} placeholder="Sélectionner un patient" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (<div className="flex items-center justify-between"><span className="text-slate-500">{k}</span><span className="text-slate-900 font-medium">{v}</span></div>);

function FicheSideCol({ patient, summary, liveTotal, liveSolde, prevSoin, nextSoin, soin, eur, onOpenPatient, onOpenPrev, onOpenNext, onSaveSuivi }) {
  const Card = ({ title, children }) => (
    <div className="sx-card p-4 mb-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--muted-2)", letterSpacing: ".06em" }}>{title}</div>
      {children}
    </div>
  );
  const solde = liveSolde != null ? liveSolde : (summary?.solde || 0);
  return (
    <div className="sticky top-4" data-testid="fs-side-col">
      <Card title="Patient">
        {patient ? (
          <>
            <button onClick={onOpenPatient} data-testid="fs-side-patient" className="font-semibold text-[14px] hover:underline text-left" style={{ color: "var(--ink)" }}>{patient.prenom} {patient.nom}</button>
            <div className="mono text-[11.5px] mt-0.5" style={{ color: "var(--id)" }}>{patient.id}</div>
            {patient.telephone && <div className="text-[12.5px] mt-1.5" style={{ color: "var(--text-2)" }}>{patient.telephone}</div>}
            {patient.email && <div className="text-[12.5px]" style={{ color: "var(--text-2)" }}>{patient.email}</div>}
          </>
        ) : <div className="text-[12.5px]" style={{ color: "var(--faint)" }}>Aucun patient</div>}
      </Card>
      <Card title="Règlement">
        <div className="space-y-1 text-[12.5px]">
          <Row k="Total" v={eur(liveTotal != null ? liveTotal : (summary?.total_ttc || 0))} />
          <Row k="Réglé" v={eur(summary?.total_paye || 0)} />
        </div>
        <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: "1px solid var(--divider)" }}>
          <span className="text-[12.5px]" style={{ color: "var(--muted)" }}>Solde</span>
          <span className="font-semibold text-[13.5px]" data-testid="fs-side-solde" style={{ color: solde > 0 ? "oklch(0.55 0.14 55)" : "oklch(0.45 0.11 155)" }}>{eur(solde)}</span>
        </div>
      </Card>
      <Card title="Prochain suivi">
        <input type="date" data-testid="fs-suivi-date" value={(soin.prochain_suivi_date || "").slice(0, 10)} onChange={(e) => onSaveSuivi({ prochain_suivi_date: e.target.value || null })} className="sx-input w-full" style={{ fontSize: 13, height: 34 }} />
        <input data-testid="fs-suivi-objet" defaultValue={soin.prochain_suivi_objet || ""} key={soin.id + (soin.prochain_suivi_objet || "")} onBlur={(e) => onSaveSuivi({ prochain_suivi_objet: e.target.value })} placeholder="Objet du suivi (bref)" className="sx-input w-full mt-1.5" style={{ fontSize: 13, height: 34 }} />
        {soin.prochain_suivi_date && <div className="text-[11.5px] mt-1" style={{ color: "var(--muted-2)" }}>Le patient remonte dans « Suivi thérapeutique ».</div>}
      </Card>
      <Card title="Séance précédente">
        {prevSoin ? (
          <button data-testid="fs-side-prev" onClick={() => onOpenPrev(prevSoin.id)} className="text-left w-full hover:underline">
            <div className="mono text-[11.5px]" style={{ color: "var(--id)" }}>{prevSoin.date ? new Date(prevSoin.date).toLocaleDateString("fr-FR") : "—"}</div>
            <div className="text-[13px] font-medium" style={{ color: (prevSoin.titre || "").trim() ? "var(--ink)" : "var(--faint)" }}>{(prevSoin.titre || "").trim() || "Sans titre"}</div>
          </button>
        ) : <div className="text-[12.5px]" style={{ color: "var(--faint)" }}>Aucune séance antérieure</div>}
      </Card>
      <Card title="Séance suivante">
        {nextSoin ? (
          <button data-testid="fs-side-next" onClick={() => onOpenNext(nextSoin.id)} className="text-left w-full hover:underline">
            <div className="mono text-[11.5px]" style={{ color: "var(--id)" }}>{nextSoin.date ? new Date(nextSoin.date).toLocaleDateString("fr-FR") : "—"}</div>
            <div className="text-[13px] font-medium" style={{ color: (nextSoin.titre || "").trim() ? "var(--ink)" : "var(--faint)" }}>{(nextSoin.titre || "").trim() || "Sans titre"}</div>
          </button>
        ) : <div className="text-[12.5px]" style={{ color: "var(--faint)" }}>Aucune séance ultérieure</div>}
      </Card>
    </div>
  );
}

function DureeTool({ value, startISO, onSet }) {
  const [open, setOpen] = useState(false);
  const [num, setNum] = useState(value);
  const boxRef = useRef(null);
  const timeOf = (iso) => { try { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; } catch { return "09:00"; } };
  const [debut, setDebut] = useState(startISO ? timeOf(startISO) : "09:00");
  const [fin, setFin] = useState("");
  useEffect(() => { setNum(value); }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const compute = () => {
    if (!debut || !fin) return;
    const [h1, m1] = debut.split(":").map(Number); const [h2, m2] = fin.split(":").map(Number);
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1); if (mins < 0) mins += 24 * 60;
    if (mins > 0) { onSet(mins); setOpen(false); }
  };
  return (
    <div ref={boxRef} className="flex items-center gap-2 relative">
      <input type="number" data-testid="fs-rdv-duree" value={num} onChange={(e) => setNum(e.target.value)} onBlur={() => { const v = Number(num); if (v > 0) onSet(v); }} className="w-20 px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm text-right" />
      <span className="text-slate-400 text-sm">min</span>
      <button data-testid="fs-rdv-duree-calc" onClick={() => setOpen((o) => !o)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" title="Calculer depuis les heures"><Clock className="w-4 h-4 text-slate-500" /></button>
      {open && (
        <div className="absolute right-0 top-10 z-20 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-56 space-y-2">
          <div className="flex items-center gap-2 text-sm"><span className="w-12 text-slate-500">Début</span><input type="time" data-testid="fs-rdv-duree-debut" value={debut} onChange={(e) => setDebut(e.target.value)} className="flex-1 px-2 py-1.5 rounded-lg border border-slate-300 text-sm" /></div>
          <div className="flex items-center gap-2 text-sm"><span className="w-12 text-slate-500">Fin</span><input type="time" data-testid="fs-rdv-duree-fin" value={fin} onChange={(e) => setFin(e.target.value)} className="flex-1 px-2 py-1.5 rounded-lg border border-slate-300 text-sm" /></div>
          <button data-testid="fs-rdv-duree-apply" onClick={compute} className="w-full px-3 py-1.5 rounded-lg bg-[#141821] text-white text-sm font-medium">Calculer la durée</button>
        </div>
      )}
    </div>
  );
}

function InlineTitle({ value, onChange, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  if (editing) return (
    <input autoFocus data-testid="fs-titre" value={val}
      onChange={(e) => { setVal(e.target.value); onChange(e.target.value); }}
      onBlur={() => { setEditing(false); onCommit(val); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setEditing(false); onCommit(val); } if (e.key === "Escape") { setVal(value); setEditing(false); } }}
      className="sx-inline-input text-[26px] font-bold tracking-[-0.02em]" style={{ width: "100%" }} />
  );
  const empty = !value;
  return (
    <div data-testid="fs-titre" onClick={() => setEditing(true)} className="sx-inline text-[26px] font-bold tracking-[-0.02em] inline-block" style={{ color: empty ? "var(--faint)" : "var(--ink)", cursor: "text" }} title="Cliquer pour modifier">
      {empty ? "Sans titre" : <span className="sx-hl">{value}</span>}
    </div>
  );
}

function SaveIndicator({ state, at }) {
  const [, f] = useState(0);
  useEffect(() => { const t = setInterval(() => f((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  const rel = (ts) => { if (!ts) return ""; const m = Math.floor((Date.now() - ts) / 60000); return m < 1 ? "à l'instant" : m < 60 ? `il y a ${m} min` : `il y a ${Math.floor(m / 60)} h`; };
  if (state === "saving") return <span className="text-[12px]" style={{ color: "var(--muted)" }} data-testid="fs-save-state">Enregistrement…</span>;
  if (state === "error") return <span className="text-[12px]" style={{ color: "oklch(0.50 0.14 25)" }} data-testid="fs-save-state">Échec · Réessayer</span>;
  if (state === "saved") return <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--muted)" }} data-testid="fs-save-state"><Check className="w-3.5 h-3.5" />Enregistré · {rel(at)}</span>;
  return null;
}

function TtcCell({ l, ro, testid, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState("");
  const display = lineTtc(l);
  return (
    <input
      data-testid={testid}
      type="number"
      step="0.01"
      disabled={ro}
      className="sx-fact-input mono text-right font-semibold"
      style={{ color: "var(--ink)" }}
      value={editing ? v : display.toFixed(2)}
      onFocus={() => { setEditing(true); setV(display.toFixed(2)); }}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { setEditing(false); onCommit(v); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}

function VoiceControls({ soinId, onInsert }) {
  return (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <VoiceRecorderButton soinId={soinId} onText={onInsert} testId="fs-vocal" />
      <span className="text-[11px]" style={{ color: "var(--muted-2)" }}>Le texte est inséré dans la fiche</span>
    </div>
  );
}
